import { useState, useCallback, useRef, useEffect } from 'react';
import { KaijuId, Algorithm, Patient, TriageTag, generatePatient, getWaveConfig, generateWaveTags, KAIJU, resetInjuryBags } from '@/lib/gameData';
import { loadCustomCases, customCaseToPatient } from '@/lib/customCases';
import { logGameEvent } from '@/lib/analytics';

export type GamePhase = 'setup' | 'loading' | 'intro' | 'tutorial' | 'playing' | 'wave-review' | 'wave-transition' | 'outro' | 'ended' | 'highscores' | 'view-highscores';

interface GameStats {
  score: number;
  correct: number;
  wrong: number;
  expired: number;
  total: number;
  livesSaved: number;
  preventableDeteriorations: number;
  preventableDeaths: number;
  unnecessarySteps: number;
}

interface GameState {
  phase: GamePhase;
  playerName: string;
  cityName: string;
  kaijuId: KaijuId | null;
  algorithm: Algorithm;
  useCustomCases: boolean;
  wave: number;
  patients: Patient[];
  visibleCount: number;
  selectedPatientId: string | null;
  stats: GameStats;
  _isLastWave: boolean;
}

const MAX_CASCADE_PER_WAVE = 3;

export function useGameState() {
  const [state, setState] = useState<GameState>({
    phase: 'setup',
    playerName: '',
    cityName: '',
    kaijuId: null,
    algorithm: 'START',
    useCustomCases: false,
    wave: 1,
    patients: [],
    visibleCount: 0,
    selectedPatientId: null,
    stats: { score: 0, correct: 0, wrong: 0, expired: 0, total: 0, livesSaved: 0, preventableDeteriorations: 0, preventableDeaths: 0, unnecessarySteps: 0 },
    _isLastWave: false,
  });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const staggerTimeoutsRef = useRef<{ timeoutId: ReturnType<typeof setTimeout>; revealIndex: number }[]>([]);
  const waveTriagedRef = useRef(0);
  const waveTotalRef = useRef(0);
  const cascadeCountRef = useRef(0);
  const waveEndTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopStagger = useCallback(() => {
    staggerTimeoutsRef.current.forEach(s => clearTimeout(s.timeoutId));
    staggerTimeoutsRef.current = [];
  }, []);

  // Cascade: spawn extra patients on deterioration
  const tryCascade = useCallback((kaijuId: KaijuId, wave: number) => {
    const cfg = getWaveConfig(wave, kaijuId);
    if (cascadeCountRef.current >= MAX_CASCADE_PER_WAVE) return;

    if (Math.random() < cfg.cascadeChance) {
      cascadeCountRef.current++;
      const delayedPatient = generatePatient(kaijuId, wave, 'yellow');
      setState(prev => ({
        ...prev,
        patients: [...prev.patients, delayedPatient],
        visibleCount: prev.visibleCount + 1,
      }));

      // Second roll for immediate
      if (Math.random() < cfg.cascadePlusImmediateChance && cascadeCountRef.current < MAX_CASCADE_PER_WAVE) {
        cascadeCountRef.current++;
        const immediatePatient = generatePatient(kaijuId, wave, 'red');
        setState(prev => ({
          ...prev,
          patients: [...prev.patients, immediatePatient],
          visibleCount: prev.visibleCount + 1,
        }));
      }
    }
  }, []);

  // Burst + trickle spawn scheduling using waveConfig
  const scheduleSpawns = useCallback((totalPatients: number, waveNum: number, kaijuId?: KaijuId) => {
    stopStagger();

    const cfg = getWaveConfig(waveNum, kaijuId);
    const burstSize = Math.min(
      totalPatients,
      cfg.spawnBurstSize.min + Math.floor(Math.random() * (cfg.spawnBurstSize.max - cfg.spawnBurstSize.min + 1))
    );

    // Set initial burst visible immediately
    setState(prev => ({ ...prev, visibleCount: burstSize }));

    if (totalPatients <= burstSize) return;

    const remaining = totalPatients - burstSize;
    let patientsScheduled = 0;
    let cursor = 0;

    while (patientsScheduled < remaining) {
      const jitter = (Math.random() * 2 - 1) * cfg.staggerInterval.jitter;
      cursor += Math.max(2000, cfg.staggerInterval.base + jitter);

      // Trickle: 1-2 patients per interval
      const rand = Math.random();
      const rawTrickle = cfg.trickleWeights
        ? cfg.trickleWeights.findIndex(w => rand < w) + 1
        : (rand > 0.6 ? 2 : 1);
      const trickleCount = Math.min(rawTrickle, remaining - patientsScheduled);
      for (let i = 0; i < trickleCount; i++) {
        const revealIndex = burstSize + patientsScheduled + i + 1;
        const delay = cursor + i * 300;
        const timeoutId = setTimeout(() => {
          setState(prev => ({
            ...prev,
            visibleCount: Math.max(prev.visibleCount, Math.min(revealIndex, prev.patients.length)),
          }));
        }, delay);
        staggerTimeoutsRef.current.push({ timeoutId, revealIndex });
      }
      patientsScheduled += trickleCount;
    }
  }, [stopStagger]);

  // If all visible patients are triaged but wave isn't done, accelerate next spawn(s)
  const spawnNextIfIdle = useCallback(() => {
    setState(prev => {
      if (prev.phase !== 'playing') return prev;
      if (prev.visibleCount >= prev.patients.length) return prev;

      const allVisibleTriaged = prev.patients.slice(0, prev.visibleCount).every(p => p.triaged);
      if (!allVisibleTriaged) return prev;

      // Cancel and consume next 1-2 scheduled timeouts
      let toSpawn = 0;
      const maxSpawn = Math.min(2, prev.patients.length - prev.visibleCount);
      while (toSpawn < maxSpawn && staggerTimeoutsRef.current.length > 0) {
        const next = staggerTimeoutsRef.current.shift()!;
        clearTimeout(next.timeoutId);
        toSpawn++;
      }

      if (toSpawn === 0) toSpawn = 1; // fallback: always spawn at least 1

      return { ...prev, visibleCount: Math.min(prev.visibleCount + toSpawn, prev.patients.length) };
    });
  }, []);

  // Tick every 500ms to drain patient timers (only visible patients)
  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(() => {
      let shouldCascade = false;
      setState(prev => {
        if (prev.phase !== 'playing') return prev;

        let statsUpdate = { ...prev.stats };
        const updatedPatients = prev.patients.map((p, i) => {
          if (p.triaged || i >= prev.visibleCount) return p;
          // Skip countdown for green/black (no timer — value 9999)
          if (p.timerDuration >= 9999) return p;
          const newTime = p.timeRemaining - 0.5;

          if (newTime <= 0) {
            // Timer expired — outcome based on correct tag
            let scorePenalty: number;
            let outcomeMessage: string;
            let deterioratedTag: TriageTag;

            let isPreventableDeterioration = false;
            let isPreventableDeath = false;
            switch (p.correctTag) {
              case 'green':
                scorePenalty = 5;
                outcomeMessage = 'Patient waited too long and left';
                deterioratedTag = 'yellow';
                break;
              case 'yellow':
                scorePenalty = 10;
                outcomeMessage = 'Patient Deteriorated — Delayed Too Long';
                deterioratedTag = 'red';
                isPreventableDeterioration = true;
                break;
              case 'red':
                scorePenalty = 20;
                outcomeMessage = 'Patient Lost — Delayed Too Long';
                deterioratedTag = 'black';
                isPreventableDeath = true;
                break;
              case 'black':
              default:
                scorePenalty = 0;
                outcomeMessage = 'Patient Expired — Delayed But No Action Changes Outcome';
                deterioratedTag = 'black';
                break;
            }

            statsUpdate = {
              ...statsUpdate,
              expired: statsUpdate.expired + 1,
              total: statsUpdate.total + 1,
              score: statsUpdate.score - scorePenalty,
              preventableDeteriorations: statsUpdate.preventableDeteriorations + (isPreventableDeterioration ? 1 : 0),
              preventableDeaths: statsUpdate.preventableDeaths + (isPreventableDeath ? 1 : 0),
            };
            waveTriagedRef.current++;
            shouldCascade = true;
            return { ...p, timeRemaining: 0, triaged: true, deteriorated: true, status: 'critical' as const, deterioratedTag, outcomeMessage };
          }

          // Deterioration: at 40% timer, stable patients become deteriorating
          const ratio = newTime / p.timerDuration;
          let newStatus = p.status;
          if (ratio < 0.4 && p.status === 'stable') newStatus = 'deteriorating';
          if (ratio < 0.2 && p.status === 'deteriorating') newStatus = 'critical';

          return { ...p, timeRemaining: newTime, status: newStatus };
        });

        return { ...prev, patients: updatedPatients, stats: statsUpdate };
      });

      // Cascade on expiry-based deterioration
      if (shouldCascade) {
        setState(prev => {
          if (prev.kaijuId) tryCascade(prev.kaijuId, prev.wave);
          return prev;
        });
      }

      // After expiry processing, check if we need to spawn more
      spawnNextIfIdle();
    }, 500);
  }, [stopTimer, spawnNextIfIdle, tryCascade]);

  const startGame = useCallback((playerName: string, cityName: string, kaijuId: KaijuId, algorithm: Algorithm) => {
    logGameEvent({ event_type: 'game_started', kaiju_id: kaijuId, algorithm });
    setState(prev => ({
      ...prev,
      playerName,
      cityName,
      kaijuId,
      algorithm,
      useCustomCases: false,
      phase: 'loading',
    }));
  }, []);

  const completeLoading = useCallback(() => {
    setState(prev => ({ ...prev, phase: 'intro' }));
  }, []);

  const completeIntro = useCallback(() => {
    setState(prev => ({ ...prev, phase: 'tutorial' }));
  }, []);

  const generateWavePatients = useCallback((kaijuId: KaijuId, wave: number, useCustom: boolean): Patient[] => {
    const { patientCount } = getWaveConfig(wave, kaijuId);
    const tags = generateWaveTags(wave, patientCount);

    if (useCustom) {
      const customCases = loadCustomCases();
      const shuffled = [...customCases].sort(() => Math.random() - 0.5);
      const patients: Patient[] = [];
      for (let i = 0; i < patientCount; i++) {
        if (i < shuffled.length) {
          patients.push(customCaseToPatient(shuffled[i], wave));
        } else {
          patients.push(generatePatient(kaijuId, wave, tags[i]));
        }
      }
      return patients;
    }
    return tags.map(tag => generatePatient(kaijuId, wave, tag));
  }, []);

  const startPlaying = useCallback(() => {
    resetInjuryBags();
    setState(prev => {
      if (!prev.kaijuId) return prev;
      return { ...prev, phase: 'wave-transition', wave: 0, patients: [], visibleCount: 0 };
    });
  }, []);

  const selectPatient = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, selectedPatientId: id }));
  }, []);

  const assignTag = useCallback((patientId: string, tag: TriageTag, penaltyPoints: number = 0, penaltyMessages: string[] = []) => {
    let didDeteriorate = false;
    setState(prev => {
      const patient = prev.patients.find(p => p.id === patientId);
      if (!patient || patient.triaged) return prev;

      const isCorrect = tag === patient.correctTag;
      const timerRatio = patient.timeRemaining / patient.timerDuration;
      const DETERIORATION_CHAIN: Record<TriageTag, TriageTag> = { green: 'yellow', yellow: 'red', red: 'black', black: 'black' };

      let pointDelta: number;
      let outcomeMessage: string | undefined;
      let deterioratedTag: TriageTag | undefined;
      let deteriorated = false;

      let preventableDeathDelta = 0;
      let preventableDeteriorationDelta = 0;

      if (isCorrect) {
        pointDelta = timerRatio > 0.3 ? 10 : 5;
      } else {
        // Determine severity of wrong tag
        const TAG_ORDER: Record<TriageTag, number> = { green: 0, yellow: 1, red: 2, black: 3 };
        const correctLevel = TAG_ORDER[patient.correctTag];
        const assignedLevel = TAG_ORDER[tag];
        const diff = Math.abs(correctLevel - assignedLevel);

        if (patient.correctTag === 'black') {
          if (tag === 'red') {
            pointDelta = -10;
            outcomeMessage = 'Patient Expectant — Another Patient Expired Due To Diverted Resources';
            preventableDeathDelta = 1;
            didDeteriorate = true;
          } else if (tag === 'yellow') {
            pointDelta = -10;
            outcomeMessage = 'Patient Expectant — Another Patient Deteriorated Due To Diverted Resources';
            preventableDeteriorationDelta = 1;
            didDeteriorate = true;
          } else {
            // GREEN assigned to BLACK
            pointDelta = -5;
            outcomeMessage = 'Critical Mistag — Patient Expectant';
            deterioratedTag = 'black';
          }
        } else if (assignedLevel < correctLevel) {
          // Undertriage: patient doesn't get enough care
          if (diff >= 2) {
            pointDelta = -20;
            outcomeMessage = 'Patient Expired — Undertriaged';
            deterioratedTag = 'black';
            deteriorated = true;
            preventableDeathDelta = 1;
          } else {
            pointDelta = -10;
             outcomeMessage = patient.correctTag === 'yellow' ? 'Patient Deteriorated — Undertriaged' : 'Patient Expired — Undertriaged';
            deterioratedTag = DETERIORATION_CHAIN[patient.correctTag];
            deteriorated = true;
            if (patient.correctTag === 'red') {
              preventableDeathDelta = 1;
            } else if (patient.correctTag === 'yellow') {
              preventableDeteriorationDelta = 1;
            }
          }
        } else {
          // Overtriage: resources wasted on this patient
          if (diff >= 3) {
            // Extreme overtriage (green → black)
            pointDelta = -15;
            outcomeMessage = 'CRITICAL MISTAG — Patient Declared Expectant, Still Walking';
            preventableDeathDelta = 1;
          } else if (tag === 'black') {
            // Salvageable patient declared expectant
            pointDelta = -12;
            outcomeMessage = 'CRITICAL MISTAG — Salvageable Patient Declared Expectant';
            if (patient.correctTag === 'red' || patient.correctTag === 'green') {
              preventableDeathDelta = 1;
            } else if (patient.correctTag === 'yellow') {
              preventableDeteriorationDelta = 1;
            }
          } else if (tag === 'red') {
            pointDelta = -8;
            outcomeMessage = 'OVERTRIAGE — Another Patient Expired Due To Diverted Resources';
            if (patient.correctTag === 'green' || patient.correctTag === 'yellow') {
              preventableDeathDelta = 1;
            }
          } else if (tag === 'yellow' && patient.correctTag === 'green') {
            pointDelta = -8;
            outcomeMessage = 'OVERTRIAGE — Another Patient Deteriorated, Resources Diverted';
            preventableDeteriorationDelta = 1;
          } else {
            pointDelta = -8;
            outcomeMessage = 'OVERTRIAGE — Another Patient Deteriorated, Resources Diverted';
          }
          deterioratedTag = undefined;
          deteriorated = false;
          didDeteriorate = true;
        }
      }

      // Subtract penalty points from the triage flow
      pointDelta -= penaltyPoints;

      if (deteriorated) didDeteriorate = true;

      const livesSavedDelta = isCorrect && patient.correctTag !== 'black' ? 1 : 0;

      const newStats = {
        ...prev.stats,
        score: prev.stats.score + pointDelta,
        correct: prev.stats.correct + (isCorrect ? 1 : 0),
        wrong: prev.stats.wrong + (isCorrect ? 0 : 1),
        total: prev.stats.total + 1,
        livesSaved: prev.stats.livesSaved + livesSavedDelta,
        preventableDeaths: prev.stats.preventableDeaths + preventableDeathDelta,
        preventableDeteriorations: prev.stats.preventableDeteriorations + preventableDeteriorationDelta,
        unnecessarySteps: prev.stats.unnecessarySteps + penaltyMessages.length,
      };

      const updatedPatients = prev.patients.map(p =>
        p.id === patientId ? { ...p, triaged: true, assignedTag: tag, deteriorated, deterioratedTag, outcomeMessage, pointDelta, penaltyMessages: penaltyMessages.length > 0 ? penaltyMessages : undefined } : p
      );

      waveTriagedRef.current++;

      return {
        ...prev,
        patients: updatedPatients,
        stats: newStats,
        selectedPatientId: null,
      };
    });

    // Cascade on mistriage deterioration
    if (didDeteriorate) {
      setState(prev => {
        if (prev.kaijuId) tryCascade(prev.kaijuId, prev.wave);
        return prev;
      });
    }

    // After triage, check if we need to spawn more patients
    spawnNextIfIdle();
  }, [spawnNextIfIdle, tryCascade]);

  // Check if wave is complete (all visible patients triaged and all revealed)
  useEffect(() => {
    if (state.phase !== 'playing') return;
    if (state.visibleCount < state.patients.length) return;
    const allTriaged = state.patients.every(p => p.triaged);
    if (!allTriaged) return;

    stopTimer();
    stopStagger();

    // Delay transition so player can review feedback on last patient
    if (waveEndTimeoutRef.current) clearTimeout(waveEndTimeoutRef.current);
    waveEndTimeoutRef.current = setTimeout(() => {
    const totalWaves = state.kaijuId ? getWaveConfig(state.wave, state.kaijuId).totalWaves : 5;
      setState(prev => ({ ...prev, phase: 'wave-review', _isLastWave: state.wave >= totalWaves }));
    }, 3000);

    return () => {
      if (waveEndTimeoutRef.current) {
        clearTimeout(waveEndTimeoutRef.current);
        waveEndTimeoutRef.current = null;
      }
    };
  }, [state.patients, state.phase, state.wave, state.visibleCount, stopTimer, stopStagger]);

  const nextWave = useCallback(() => {
    let patients: Patient[] = [];
    let wave = 1;
    let kaijuId: KaijuId | null = null;
    setState(prev => {
      if (!prev.kaijuId) return prev;
      wave = prev.wave + 1;
      kaijuId = prev.kaijuId;
      patients = generateWavePatients(prev.kaijuId, wave, prev.useCustomCases);
      waveTriagedRef.current = 0;
      waveTotalRef.current = patients.length;
      cascadeCountRef.current = 0;
      return { ...prev, phase: 'playing', wave, patients, visibleCount: 0, selectedPatientId: null };
    });
    startTimer();
    setTimeout(() => {
      scheduleSpawns(patients.length, wave, kaijuId ?? undefined);
    }, 0);
  }, [startTimer, generateWavePatients, scheduleSpawns]);

  const resetGame = useCallback(() => {
    stopTimer();
    stopStagger();
    if (waveEndTimeoutRef.current) { clearTimeout(waveEndTimeoutRef.current); waveEndTimeoutRef.current = null; }
    cascadeCountRef.current = 0;
    resetInjuryBags();
    setState({
      phase: 'setup',
      playerName: '',
      cityName: '',
      kaijuId: null,
      algorithm: 'START',
      useCustomCases: false,
      wave: 1,
      patients: [],
      visibleCount: 0,
      selectedPatientId: null,
      stats: { score: 0, correct: 0, wrong: 0, expired: 0, total: 0, livesSaved: 0, preventableDeteriorations: 0, preventableDeaths: 0, unnecessarySteps: 0 },
      _isLastWave: false,
    });
  }, [stopTimer, stopStagger]);

  const resetBattle = useCallback(() => {
    stopTimer();
    stopStagger();
    if (waveEndTimeoutRef.current) { clearTimeout(waveEndTimeoutRef.current); waveEndTimeoutRef.current = null; }
    waveTriagedRef.current = 0;
    waveTotalRef.current = 0;
    cascadeCountRef.current = 0;
    resetInjuryBags();
    setState(prev => ({
      ...prev,
      phase: 'wave-transition',
      wave: 0,
      patients: [],
      visibleCount: 0,
      selectedPatientId: null,
      stats: { score: 0, correct: 0, wrong: 0, expired: 0, total: 0, livesSaved: 0, preventableDeteriorations: 0, preventableDeaths: 0, unnecessarySteps: 0 },
    }));
  }, [stopTimer, stopStagger]);

  const completeWaveReview = useCallback(() => {
    setState(prev => {
      if (prev._isLastWave) {
        return { ...prev, phase: 'outro' };
      }
      return { ...prev, phase: 'wave-transition' };
    });
  }, []);

  const completeOutro = useCallback(() => {
    setState(prev => {
      logGameEvent({
        event_type: 'game_completed',
        kaiju_id: prev.kaijuId,
        algorithm: prev.algorithm,
        score: Math.max(0, Math.min(5000, prev.stats.score)),
      });
      return { ...prev, phase: 'ended' };
    });
  }, []);

  const showHighScores = useCallback((mode?: 'view') => {
    setState(prev => ({ ...prev, phase: mode === 'view' ? 'view-highscores' : 'highscores' }));
  }, []);

  const accuracy = state.stats.total > 0
    ? Math.round((state.stats.correct / state.stats.total) * 100)
    : 0;

  const selectedPatient = state.patients.find(p => p.id === state.selectedPatientId) || null;
  const visiblePatients = state.patients.slice(0, state.visibleCount);

  const pauseGame = useCallback(() => {
    stopTimer();
  }, [stopTimer]);

  const resumeGame = useCallback(() => {
    startTimer();
  }, [startTimer]);

  return {
    ...state,
    accuracy,
    selectedPatient,
    visiblePatients,
    startGame,
    completeLoading,
    completeIntro,
    startPlaying,
    selectPatient,
    assignTag,
    nextWave,
    completeWaveReview,
    completeOutro,
    resetGame,
    resetBattle,
    showHighScores,
    pauseGame,
    resumeGame,
  };
}
